import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router'

import { Button, DrawerBodyWrapper, DrawerFooterWrapper, Input, SectionHeader, Select, Textarea, TOAST_TYPE } from '@base'
import { MB_ROLES, strings } from '@constants'
import { getAllEmployees } from '@data'
import { User } from '@data/model'
import { BiInputFieldWrapper, VotingOptionsWrapper, VotingOptionWrapper } from '../styles'
import { Delete } from '@material-ui/icons'
import { addVotingOptions, updateRecommendedCreditLimit } from '@data/conn/put/cran'
import { showLoader, showToast } from '@data/state/action'

const deriveFormState = (data) => {
	const _state = {
		credit_limit: {
			id: 'credit-limit',
			label: strings('credit_limit'),
			placeholder: strings('enter', 'credit_limit'),
			value: '',
			disabled: false,
			required: false,
		},
		credit_term: {
			id: 'credit-term',
			label: strings('credit_term'),
			placeholder: strings('enter', 'credit_term'),
			value: '',
			disabled: false,
			required: false,
		},
		commodity: {
			id: 'commodity',
			label: strings('commodity'),
			placeholder: strings('enter', 'commodity'),
			value: '',
			disabled: false,
			required: false,
		},
		created_by: {
			id: 'created-by',
			label: strings('created', 'by'),
			placeholder: strings(['select', 'created', 'by']),
			value: '',
			disabled: false,
			required: false,
		},
	}
	if (data) {
		_state.commodity.value = data?.commodity
		_state.created_by.value = data?.created_by
		_state.credit_limit.value = data?.credit_limit
		_state.credit_term.value = data?.credit_term
	}

	return _state
}

const deriveVotingFormstate = (votingData) => {
	const _state = {
		voting_options: {
			id: 'voting-options',
			label: strings('voting', 'options'),
			placeholder: strings(''),
			value: [],
			disabled: false,
			required: false,
		},
		voting_title: {
			id: 'voting-remark',
			label: strings('voting', 'title'),
			placeholder: strings('voting', 'title'),
			value: '',
			disabled: false,
			required: false,
		},
	}
	if (votingData) {
		_state.voting_options.value = votingData?.voting_options
		_state.voting_title.value = votingData?.title
	}
	return _state
}

const RecommendedLimit = ({ cranData, update }) => {
	const dispatch = useDispatch()
	const { orgId } = useParams()
	const loggedInUser = useSelector((state) => state.getIn(['user', 'user']))
	const [formState, setFormState] = useState(deriveFormState())
	const [voteFormState, setVoteFormState] = useState(deriveVotingFormstate())
	const [adminList, setAdminList] = useState([])
	const [votingOption, setVotingOption] = useState('')

	useEffect(() => {
		getAllEmployees({ roleFilter: MB_ROLES.FINANCE })
			.then((response) => {
				setAdminList(response.map((item) => new User(item).getUserDetails()))
			})
			.catch((error) => {
				console.log(error)
			})
			.finally(() => {
				if (cranData?.financial_analyst && !cranData?.recommended_credit_details) {
					updateState('created_by')(cranData?.financial_analyst)
				}
			})

		if (cranData?.recommended_credit_details) {
			setFormState(deriveFormState(cranData?.recommended_credit_details))
			setVoteFormState(deriveVotingFormstate(cranData?.voting_detail))
		} else {
			setFormState(deriveFormState())
		}
	}, [cranData])

	const updateState = (key) => (value) => {
		setFormState((_s) => ({
			..._s,
			[key]: {
				..._s[key],
				value: value,
			},
		}))
	}

	const updateVoteFormState = (key) => (value) => {
		setVoteFormState((_s) => ({
			..._s,
			[key]: {
				..._s[key],
				value: value,
			},
		}))
	}

	const addOptionHandler = () => {
		const _options = [...voteFormState?.voting_options?.value, { option: votingOption }]
		updateVoteFormState('voting_options')(_options)
		setVotingOption('')
	}

	const deleteOptionHandler = (vote) => {
		const _options = voteFormState?.voting_options?.value?.filter((item) => item?.option !== vote?.option)
		updateVoteFormState('voting_options')(_options)
	}

	const formStateToObject = () => {
		return {
			created_by: formState?.created_by?.value,
			commodity: formState?.commodity?.value,
			credit_limit: formState?.credit_limit?.value,
			credit_term: formState?.credit_term?.value,
		}
	}
	const submitCreditHandler = () => {
		dispatch(showLoader(true))

		const _recommendedLimit = formStateToObject()

		updateRecommendedCreditLimit({
			organisationId: orgId,
			userId: loggedInUser?.user_id,
			recommendedLimit: _recommendedLimit,
		})
			.then((response) => {
				dispatch(showToast(true, strings('recommended_credit_limit_updated_successfully'), { type: TOAST_TYPE.SUCCESS }))
			})
			.catch((error) => {
				dispatch(showToast(true, strings('recommended_credit_limit_update_failed'), { type: TOAST_TYPE.ERROR }))
			})
			.finally(() => {
				update()
				dispatch(showLoader(false))
			})
	}

	const isDisabled = useMemo(() => {
		return false
	}, [formState])

	const submitHandler = () => {
		dispatch(showLoader(true))

		const _title = voteFormState?.voting_title?.value
		const _votingOptions = voteFormState?.voting_options?.value
		addVotingOptions(loggedInUser?.user_id, orgId, _title, _votingOptions)
			.then((response) => {
				dispatch(showToast(true, strings('msg_voting_details_save_success'), { type: TOAST_TYPE.SUCCESS }))
			})
			.catch((error) => {
				dispatch(showToast(true, strings('msg_voting_details_save_failure'), { type: TOAST_TYPE.ERROR }))
			})
			.finally(() => {
				update()
				dispatch(showLoader(false))
			})
	}

	const renderVotingOptions = () => {
		if (voteFormState?.voting_options?.value === null || voteFormState?.voting_options?.value?.length === 0) {
			return null
		}
		return (
			<VotingOptionsWrapper>
				<SectionHeader>{strings('voting_options')}</SectionHeader>
				{voteFormState?.voting_options?.value?.map((item, index) => {
					return (
						<VotingOptionWrapper>
							<div>{item?.option}</div>
							<Button xs variant={'destructive'} onClick={() => deleteOptionHandler(item)}>
								<Delete />
							</Button>
						</VotingOptionWrapper>
					)
				})}
			</VotingOptionsWrapper>
		)
	}

	return (
		<>
			<DrawerBodyWrapper>
				<BiInputFieldWrapper>
					<Select
						id={formState?.created_by?.id}
						label={formState?.created_by?.label}
						placeholder={formState?.created_by?.placeholder}
						data={adminList ?? []}
						primaryKey='user_id'
						displayKey='full_name'
						value={adminList?.find((item) => formState?.created_by?.value?.user_id === item.user_id) ?? {}}
						onChange={updateState('created_by')}
					/>
					<Input
						id={formState?.credit_term?.id}
						label={formState?.credit_term?.label}
						placeholder={formState?.credit_term?.placeholder}
						value={formState?.credit_term?.value}
						disabled={formState?.credit_term?.disabled}
						required={formState?.credit_term?.required}
						onChange={updateState('credit_term')}
					/>
				</BiInputFieldWrapper>
				<BiInputFieldWrapper>
					<Input
						id={formState?.credit_limit?.id}
						label={formState?.credit_limit?.label}
						type={'number'}
						placeholder={formState?.credit_limit?.placeholder}
						value={formState?.credit_limit?.value}
						disabled={formState?.credit_limit?.disabled}
						required={formState?.credit_limit?.required}
						onChange={updateState('credit_limit')}
					/>
					<Input
						id={formState?.commodity?.id}
						label={formState?.commodity?.label}
						placeholder={formState?.commodity?.placeholder}
						value={formState?.commodity?.value}
						disabled={formState?.commodity?.disabled}
						required={formState?.commodity?.required}
						onChange={updateState('commodity')}
					/>
				</BiInputFieldWrapper>
				<Button xs margin={` 0  0  0 auto`} disabled={isDisabled} onClick={submitCreditHandler}>
					{strings('submit')}
				</Button>
				<SectionHeader>{strings('')}</SectionHeader>
				<Textarea
					id={voteFormState?.voting_title?.id}
					label={voteFormState?.voting_title?.label}
					placeholder={voteFormState?.voting_title?.placeholder}
					value={voteFormState?.voting_title?.value}
					disabled={voteFormState?.voting_title?.disabled}
					required={voteFormState?.voting_title?.required}
					onChange={updateVoteFormState('voting_title')}
				/>
				{renderVotingOptions()}

				<Input
					id={'voting-option'}
					label={strings('voting_option')}
					placeholder={strings(['enter', 'voting_option'])}
					value={votingOption}
					disabled={false}
					required={false}
					onChange={setVotingOption}
				/>
				<Button xs disabled={votingOption === ''} margin={`0 0 0 auto`} onClick={addOptionHandler}>
					{strings('add')}
				</Button>
			</DrawerBodyWrapper>
			<DrawerFooterWrapper>
				<Button small disabled={isDisabled} onClick={submitHandler}>
					{strings('submit')}
				</Button>
			</DrawerFooterWrapper>
		</>
	)
}

export default RecommendedLimit
